// Hook: Option chain data + Greeks via REST API
import { useState, useCallback, useEffect } from 'react';
import { useTrading } from '../context/TradingContext';
import angelOneApi from '../services/angelOneApi';

// Calculate nearest weekly expiry (Thursday for NIFTY, Wednesday for BANKNIFTY)
function getNextExpiry(underlying) {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ..., 4=Thu, 5=Fri, 6=Sat

  // NIFTY expires Thursday (4), BANKNIFTY expires Wednesday (3), FINNIFTY Tuesday (2), SENSEX Friday (5)
  let expiryDay = 4; // Thursday default
  if (underlying === 'BANKNIFTY') expiryDay = 3;
  if (underlying === 'FINNIFTY') expiryDay = 2;
  if (underlying === 'SENSEX') expiryDay = 5;

  let daysUntilExpiry = expiryDay - day;
  if (daysUntilExpiry < 0) daysUntilExpiry += 7;
  if (daysUntilExpiry === 0) {
    // Today is expiry day — use today if before market close, otherwise next week
    const hour = now.getHours();
    if (hour >= 16) daysUntilExpiry = 7;
  }

  const expiryDate = new Date(now);
  expiryDate.setDate(expiryDate.getDate() + daysUntilExpiry);

  // Format as DDMMMYYYY (e.g., 05MAR2026)
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const dd = String(expiryDate.getDate()).padStart(2, '0');
  const mmm = months[expiryDate.getMonth()];
  const yyyy = expiryDate.getFullYear();
  return `${dd}${mmm}${yyyy}`;
}

// Generate upcoming expiry dates for the next 8 weeks
function getUpcomingExpiries(underlying) {
  const expiries = [];
  const now = new Date();
  let expiryDay = 4;
  if (underlying === 'BANKNIFTY') expiryDay = 3;
  if (underlying === 'FINNIFTY') expiryDay = 2;
  if (underlying === 'SENSEX') expiryDay = 5;

  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  for (let week = 0; week < 8; week++) {
    const d = new Date(now);
    d.setDate(d.getDate() + (week * 7));

    // Find next expiry day from this date
    let daysUntil = expiryDay - d.getDay();
    if (daysUntil < 0) daysUntil += 7;
    if (daysUntil === 0 && week > 0) daysUntil = 0; // Use this day

    const expDate = new Date(d);
    expDate.setDate(expDate.getDate() + daysUntil);

    const dd = String(expDate.getDate()).padStart(2, '0');
    const mmm = months[expDate.getMonth()];
    const yyyy = expDate.getFullYear();
    const expStr = `${dd}${mmm}${yyyy}`;

    if (!expiries.includes(expStr)) {
      expiries.push(expStr);
    }
  }
  return expiries;
}

export function useOptionChain(underlyingToken, exchange = 'NFO') {
  const { connectionStatus } = useTrading();
  const [optionData, setOptionData] = useState([]);
  const [greeks, setGreeks] = useState({});
  const [expiryDates, setExpiryDates] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [spotPrice, setSpotPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pcr, setPCR] = useState(null);
  const [error, setError] = useState(null);

  // Set default expiry and expiry list on underlying change
  useEffect(() => {
    if (!underlyingToken) return;
    const defaultExpiry = getNextExpiry(underlyingToken);
    const upcoming = getUpcomingExpiries(underlyingToken);
    setExpiryDates(upcoming);
    setSelectedExpiry(defaultExpiry);
    setOptionData([]);
    setGreeks({});
    setPCR(null);
    setError(null);
  }, [underlyingToken]);

  const fetchOptionChain = useCallback(async (expiry) => {
    const expiryToUse = expiry || selectedExpiry;
    if (connectionStatus !== 'connected' || !underlyingToken || !expiryToUse) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch option Greeks — this is the main option chain data source
      const result = await angelOneApi.getOptionGreeks({
        name: underlyingToken,
        expirydate: expiryToUse,
      });

      // Check for API error response
      if (result.data?.status === false) {
        setError(result.data.message || 'Option Greeks API returned an error');
        return;
      }

      // Handle response — data could be at result.data.data or result.data
      let data = null;
      if (result.data?.data && Array.isArray(result.data.data)) {
        data = result.data.data;
      } else if (Array.isArray(result.data)) {
        data = result.data;
      }

      if (data && data.length > 0) {
        setOptionData(data);

        // Build Greeks map
        const greeksMap = {};
        data.forEach(d => {
          const strikeKey = parseFloat(d.strikePrice);
          const key = `${strikeKey}_${d.optionType}`;
          greeksMap[key] = {
            iv: parseFloat(d.impliedVolatility) || 0,
            delta: parseFloat(d.delta) || 0,
            gamma: parseFloat(d.gamma) || 0,
            theta: parseFloat(d.theta) || 0,
            vega: parseFloat(d.vega) || 0,
          };
        });
        setGreeks(greeksMap);
      } else {
        setError(`No option data for ${underlyingToken} expiry ${expiryToUse}. Market may be closed or expiry date invalid.`);
      }

      // Fetch PCR
      try {
        const pcrResult = await angelOneApi.getPCR({
          symbol: underlyingToken,
          expirydate: expiryToUse,
        });
        if (pcrResult.data?.data) {
          setPCR(pcrResult.data.data);
        }
      } catch (pcrErr) {
        // PCR is optional
      }
    } catch (e) {
      console.error('Option chain fetch failed:', e);
      setError(e.message || 'Failed to load option chain');
    } finally {
      setLoading(false);
    }
  }, [connectionStatus, underlyingToken, selectedExpiry]);

  // Fetch when expiry changes
  useEffect(() => {
    if (selectedExpiry && connectionStatus === 'connected') {
      fetchOptionChain(selectedExpiry);
    }
  }, [selectedExpiry, connectionStatus, fetchOptionChain]);

  // Process into calls/puts grid
  const chainData = optionData.reduce((acc, item) => {
    const strike = parseFloat(item.strikePrice);
    if (isNaN(strike)) return acc;
    if (!acc[strike]) acc[strike] = { strike, call: null, put: null };
    if (item.optionType === 'CE') acc[strike].call = item;
    if (item.optionType === 'PE') acc[strike].put = item;
    return acc;
  }, {});

  const strikes = Object.values(chainData).sort((a, b) => a.strike - b.strike);

  return {
    strikes,
    optionData,
    greeks,
    expiryDates,
    selectedExpiry,
    setSelectedExpiry,
    spotPrice,
    setSpotPrice,
    pcr,
    loading,
    error,
    refresh: fetchOptionChain,
  };
}
